/**
 * The end-of-workout receipt.
 *
 * A till roll, because that is the one summary layout everybody can already
 * read: a quantity, a name, a figure per line, a rule, and a total at the
 * bottom. It also happens to suit this UI exactly — a receipt is monospaced,
 * left-aligned, has no colour and no rounded corners, and is printed in capitals
 * on paper. The whole aesthetic is already in the font.
 *
 * It is itemised by SET rather than by rep. A rep's verdict has already been
 * delivered, as a sound and a banner, at the moment it mattered; printing ten of
 * them afterwards is a log, not a summary. What the athlete did not yet have is
 * the shape of the whole thing, so each set is one line with its reps and its
 * score, and each of its criteria is a sub-line with how many reps passed it.
 *
 * Under the total is the one thing on the ticket that is coaching rather than
 * accounting: what to fix next time, in the drill's own cues, or PERFECT when
 * there is nothing. See `feedbackLines()` for why a partly seen rep never earns
 * that word.
 *
 * The lines are built as data, apart from the rendering, for the same reason
 * `verdictFor()` is: the layout can then be checked against a recording from
 * Node, with no browser and no camera. `src/metrics/receipt.js` printing a
 * sensible ticket for a ten-rep take is a thing a script can assert.
 */
import { rankFor } from "./score";

function minutes(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function stamp(ms) {
  const date = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * The receipt for one workout, as lines.
 *
 * @param workout A `Workout` with at least one set.
 * @param earned Optional, per set in the same order: `{ gained, from, to }` from
 *   `Progress.add()`. The XP a receipt prints is the clean reps already on the
 *   line above it, said in the game's own words rather than counted again — and
 *   a level crossed is the one thing on the ticket worth a line of its own.
 * @return `{ kind, left, right }` rows. `kind` is what the renderer styles on:
 *   title, subtitle, meta, rule, thin, head, item, sub, grand, rank, fixhead,
 *   fix, perfect, note, footer. `left`, `right` and `qty` are already-formatted
 *   strings; `right` and `qty` may be null.
 */
export function receiptLines(workout, earned = null) {
  const lines = [];
  const push = (kind, left = null, right = null, qty = null) =>
    lines.push({ kind, left, right, qty });

  push("title", "Spottr");
  push("subtitle", "Training receipt");
  push(
    "meta",
    stamp(Date.now()),
    `${workout.sets.length} set${workout.sets.length === 1 ? "" : "s"}`
  );
  push("rule");
  push("head", "Item", "Clean", "Qty");

  workout.sets.forEach((set, index) => {
    const exercise = set.exercise || {};
    const name =
      exercise.calibrated === false
        ? `${exercise.name} *`
        : exercise.name || "Drill";
    // The quantity is its own column rather than padding on the label: HTML
    // collapses runs of spaces, so a padded string that lines up in a terminal
    // does not line up on the page.
    push("item", name, `${set.good}/${set.reps}`, String(set.reps));
    for (const criterion of set.criteria) {
      // Unjudged reps are left out of the denominator rather than counted as
      // failures: the criterion was not measured on them, which is a different
      // statement from the athlete missing it.
      const figure =
        criterion.judged === 0
          ? "--"
          : `${criterion.passed}/${criterion.judged}`;
      push("sub", criterion.label, figure);
    }
    for (const total of set.totals || []) {
      // The figures that judge nothing but add up: air time on the jump today.
      // Printed as the set's own total with its best rep beside it, which is
      // the form a score is read in.
      push(
        "sub",
        total.label,
        `${total.total.toFixed(1)}${total.unit} · best ${total.best.toFixed(2)}${total.unit}`
      );
    }
    if (set.partial > 0) {
      push("sub", "Partly seen", `${set.partial}`);
    }
    const gain = earned && earned[index];
    if (gain && gain.gained > 0) {
      push("sub", `${exercise.name} XP`, `+${gain.gained}`);
    }
    if (gain && gain.to > gain.from) {
      push("levelup", `${exercise.name} reached Lv ${gain.to}`);
    }
  });

  push("thin");
  push("total", "Reps", String(workout.reps));
  push("total", "Clean reps", String(workout.good));
  if (workout.partial > 0) {
    push("total", "Partly seen", String(workout.partial));
  }
  push("total", "Time", minutes(workout.seconds));
  for (const total of workout.totals) {
    push(
      "total",
      total.label,
      `${total.total.toFixed(1)}${total.unit} · best ${total.best.toFixed(2)}${total.unit}`
    );
  }
  push("thin");
  const percent = workout.percent;
  push("grand", "Total", percent == null ? "--" : `${percent}%`);
  push("rank", "Rank", rankFor(percent));
  push("thin");
  for (const line of feedbackLines(workout)) {
    lines.push(line);
  }
  push("rule");
  if (workout.uncalibrated) {
    // Printed on the ticket rather than only shown in the menu: a score carries
    // the authority of the thresholds behind it, and the squat's are not
    // measured off footage the way the swing's are.
    push("note", "* Experimental — the count is sound, the score is a guess");
  }
  push("barcode");
  push("footer", "Thank you — train again soon");
  // Where the ticket came from, the way a till roll prints the shop: this one
  // gets printed and kept, and off the phone it is the only thing on it that
  // says what made it.
  push("footer", "getspottr.pro");
  return lines;
}

/** At most this many faults are printed as things to fix. */
const MAX_FIXES = 3;

/**
 * What to change next time, or that there is nothing.
 *
 * The rep verdicts were delivered as they happened, so what this adds is the
 * one thing the athlete did wrong most often, said in the drill's own cue —
 * imperative and short, the same words that were on the banner mid-rep, rather
 * than a criterion name restated. Three at most: a list of eight faults is a log
 * again, and the point of the block is to leave the athlete with something they
 * can hold in their head on the way back to the mat.
 *
 * Two things it will not do. **A partly seen rep is not a perfect one**, even
 * though it counts as clean and can carry a 100% total: nothing failed because
 * the criteria were not measured, and telling someone they were perfect on reps
 * the model was guessing at is the one kind of praise this app must not give. So
 * framing is a line in the fix list, in plain words rather than as a drill cue,
 * since no exercise declares one for it. And **PERFECT says nothing about the
 * thresholds behind it** — on an uncalibrated drill it is a perfect score
 * against numbers nobody measured, which is what the experimental note under it
 * is for. The note is not softened and PERFECT is not withheld: the mark is the
 * athlete's performance against what the app claimed, and the asterisk is the
 * app's claim about itself.
 */
function feedbackLines(workout) {
  const lines = [];
  const push = (kind, left = null, right = null) =>
    lines.push({ kind, left, right, qty: null });
  const faults = workout.topFaults;
  if (faults.length === 0 && workout.partial === 0) {
    push("perfect", "Perfect");
    push("note", "Nothing to fix - every rep clean and fully seen");
    return lines;
  }
  push("fixhead", "Fix next time");
  for (const fault of faults.slice(0, MAX_FIXES)) {
    push("fix", fault.cue, `x${fault.count}`);
  }
  const dropped = faults.length - MAX_FIXES;
  if (dropped > 0) {
    push("note", `+${dropped} more above`);
  }
  if (workout.partial > 0) {
    push("fix", "Get your whole body in shot", `x${workout.partial}`);
  }
  return lines;
}

/**
 * Renders the receipt into the DOM.
 *
 * DOM and not canvas, for the reason every other panel in this app is: the
 * canvas context is permanently mirrored, so text painted through it comes out
 * backwards, and real buttons get touch targets and screen-reader semantics for
 * free.
 */
export class Receipt {
  /**
   * @param handlers.onAgain Back to the drill to add another set.
   * @param handlers.onMenu Back to the exercise menu.
   */
  constructor({ onAgain, onMenu }) {
    this.panel = document.getElementById("receipt");
    this.paper = document.getElementById("receipt-paper");
    this.again = document.getElementById("receipt-again");
    this.menu = document.getElementById("receipt-menu");
    this.printButton = document.getElementById("receipt-print");
    this.again.addEventListener("click", () => {
      this.hide();
      onAgain();
    });
    this.menu.addEventListener("click", () => {
      this.hide();
      onMenu();
    });
    if (this.printButton) {
      // The ticket stays open behind the print dialog: the athlete cancels or
      // prints and is still looking at the same workout. The page-sized version
      // is `@media print` in base.css, not a second rendering of the lines.
      this.printButton.addEventListener("click", () => window.print());
    }
  }

  get visible() {
    return !this.panel.hidden;
  }

  show(workout, earned = null) {
    this.paper.textContent = "";
    for (const line of receiptLines(workout, earned)) {
      this.paper.appendChild(renderLine(line));
    }
    this.panel.hidden = false;
    // So the pad's buttons are reachable by keyboard the moment it opens, and
    // because the drill's Space shortcut must not fall through to Start.
    this.again.focus();
  }

  hide() {
    this.panel.hidden = true;
  }
}

function renderLine(line) {
  if (line.kind === "rule" || line.kind === "thin") {
    const rule = document.createElement("div");
    rule.className = `receipt-${line.kind}`;
    return rule;
  }
  if (line.kind === "barcode") {
    return renderBarcode();
  }
  const row = document.createElement("div");
  row.className = `receipt-line receipt-${line.kind}`;
  if (line.qty != null) {
    const qty = document.createElement("span");
    qty.className = "receipt-qty";
    qty.textContent = line.qty;
    row.appendChild(qty);
  }
  const left = document.createElement("span");
  left.className = "receipt-left";
  left.textContent = line.left;
  row.appendChild(left);
  if (line.right != null) {
    const right = document.createElement("span");
    right.className = "receipt-right";
    right.textContent = line.right;
    row.appendChild(right);
  }
  return row;
}

/**
 * The barcode. It encodes nothing and is not pretending to: it is the one mark
 * that makes a block of monospaced text unmistakably a till receipt, and it is
 * built from hard-edged bars, which is the only kind of gradient this UI allows.
 */
function renderBarcode() {
  const bars = document.createElement("div");
  bars.className = "receipt-barcode";
  bars.setAttribute("aria-hidden", "true");
  const widths = [3, 1, 2, 1, 1, 4, 1, 2, 3, 1, 1, 2, 4, 1, 3, 1, 2, 1, 1, 3];
  widths.forEach((width, i) => {
    const bar = document.createElement("span");
    bar.style.width = `${width * 2}px`;
    // Alternating bar and gap, the way a real one reads.
    bar.style.background = i % 2 === 0 ? "var(--edge)" : "transparent";
    bars.appendChild(bar);
  });
  return bars;
}
